/**
 * Pitidos del cronómetro. Se sintetizan con Web Audio: sin ficheros que
 * descargar y sin latencia la primera vez que suena.
 *
 * iOS exige que el contexto se cree o se reanude dentro de un gesto del
 * usuario, de ahí `unlock()`, que se llama al pulsar Empezar.
 */

type CueName = 'start' | 'tick' | 'stepChange' | 'rest' | 'count' | 'finish'

interface Tone {
  readonly frequency: number
  readonly seconds: number
  readonly volume: number
  /** Retraso desde el disparo, para encadenar dos notas. */
  readonly delay?: number
}

const CUES: Record<CueName, readonly Tone[]> = {
  start: [{ frequency: 660, seconds: 0.1, volume: 0.22 }],
  tick: [{ frequency: 1046, seconds: 0.06, volume: 0.24 }],
  stepChange: [{ frequency: 980, seconds: 0.2, volume: 0.3 }],
  rest: [{ frequency: 520, seconds: 0.2, volume: 0.3 }],
  count: [{ frequency: 760, seconds: 0.07, volume: 0.2 }],
  finish: [
    { frequency: 980, seconds: 0.35, volume: 0.32 },
    { frequency: 1240, seconds: 0.35, volume: 0.28, delay: 0.15 },
  ],
}

class AudioCues {
  #context: AudioContext | null = null
  #muted = false

  get muted(): boolean {
    return this.#muted
  }

  setMuted(muted: boolean): void {
    this.#muted = muted
  }

  /** Prepara el audio dentro de un gesto del usuario. Idempotente. */
  unlock(): void {
    const context = this.#ensureContext()
    if (context?.state === 'suspended') void context.resume()
  }

  play(cue: CueName): void {
    if (this.#muted) return
    const context = this.#ensureContext()
    if (!context) return
    if (context.state === 'suspended') void context.resume()

    for (const tone of CUES[cue]) {
      this.#tone(context, tone)
    }
  }

  #ensureContext(): AudioContext | null {
    if (this.#context) return this.#context
    try {
      const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      this.#context = new Ctor()
      return this.#context
    } catch {
      return null
    }
  }

  #tone(context: AudioContext, tone: Tone): void {
    try {
      const startAt = context.currentTime + (tone.delay ?? 0)
      const oscillator = context.createOscillator()
      const gain = context.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.value = tone.frequency
      gain.gain.setValueAtTime(tone.volume, startAt)
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + tone.seconds)

      oscillator.connect(gain)
      gain.connect(context.destination)
      oscillator.start(startAt)
      oscillator.stop(startAt + tone.seconds)
    } catch {
      // Si el navegador no deja sonar, el entreno continúa en silencio.
    }
  }
}

export const audioCues = new AudioCues()
export type { CueName }
